import { useApi } from '@/shared/api/http';
import type { RequestParams } from '@/shared/model/types/http';
import type {
  IGetLawyerArticlesParams,
  IArticlePayload,
} from '@/entities/journal/model/types/journal';
import type {
  IJournalArticle,
  IJournalTopic,
  IJournalTopicSection,
  IJournalComment,
  IJournalSearchResult,
  IChiefEditorResponse,
  IJournalAuthor,
  IJournalTopicSearchPayload,
  IJournalCommentPayload,
} from '@/entities/journal/model/types';

export const useJournalService = () => {
  const api = useApi();

  const getAllLawyerArticles = async (params: IGetLawyerArticlesParams) => {
    return await api.get('journal-moderation/', { params: { ...params } });
  };

  const getEditableLawyerArticle = async (articleId: number) => {
    return await api.get(`journal-moderation/${articleId}/`);
  };

  const createArticle = async (dataReq: IArticlePayload) => {
    return await api.post('journal-moderation', dataReq);
  };

  const updateArticle = async (articleId: number, dataReq: IArticlePayload) => {
    return await api.patch(`journal-moderation/${articleId}`, dataReq);
  };

  const getArticles = async <T = IJournalArticle[] | IJournalTopicSection[]>(
    params: RequestParams,
  ) => {
    return await api.get<T>('journal/', { params });
  };

  const getArticleBySlug = async (slug: string) => {
    return await api.get<IJournalArticle | null>(`journal/${slug}/`);
  };

  const sendArticleView = async (slug: string) => {
    return await api.post(`journal/${slug}/view`);
  };

  const getTopics = async (params: RequestParams) => {
    return await api.get<IJournalTopic[]>('topic/', { params });
  };

  const searchJournalTopics = async (dataReq: IJournalTopicSearchPayload) => {
    return await api.post<IJournalSearchResult>('journal-topic/search', dataReq);
  };

  const getComments = async (params: RequestParams) => {
    return await api.get<IJournalComment[]>('journal-comment/', { params });
  };

  const createComment = async (dataReq: IJournalCommentPayload) => {
    return await api.post<IJournalComment | null>('journal-comment', dataReq);
  };

  const editComment = async (
    commentId: number | string,
    dataReq: IJournalCommentPayload,
  ) => {
    return await api.patch<IJournalComment | null>(
      `journal-comment/${commentId}`,
      dataReq,
    );
  };

  const getChiefEditor = async () => {
    return await api.get<IChiefEditorResponse>('journal-editorial/chief-editor/');
  };

  const getAuthors = async (params: RequestParams) => {
    return await api.get<IJournalAuthor[]>('journal-author/', { params });
  };

  return {
    getAllLawyerArticles,
    getEditableLawyerArticle,
    createArticle,
    updateArticle,
    getArticles,
    getArticleBySlug,
    sendArticleView,
    getTopics,
    searchJournalTopics,
    getComments,
    createComment,
    editComment,
    getChiefEditor,
    getAuthors,
  };
};
